import { t } from '@trimble/i18n';
import { readModusInputChangeValue } from '@trimble/utilities';
import { Component, Show } from 'solid-js';
import { ColorSwatch } from '../../components/ColorSwatch';
import { PALETTE_STOP_NUMBER_INPUT_CLASS } from '../palette-stop-number-input-styles';
import { blockPaletteStopExponentKey } from '../palette-stop-value-input';

const NEW_STOP_NUMBER_INPUT_CLASS =
  'flex-1 min-w-0 h-7 max-h-7 min-h-0 [&_.modus-wc-input]:mb-0 [&_.modus-wc-input]:h-full [&_.modus-wc-input]:max-h-full [&_.modus-wc-input]:min-h-0 [&_input]:h-7 [&_input]:max-h-7 [&_input]:min-h-0 [&_input]:py-0 [&_input]:text-sm [&_input]:leading-7';

/** Modus error border without rendering the feedback row (handled separately). */
const NEW_STOP_INPUT_ERROR_CLASS = 'modus-wc-input--error';

interface NewColorStopInputProps {
  /** Current text in the value input (controlled). */
  readonly value: string;
  /** Length unit suffix shown after the value (e.g. 'm', 'ft'). Defaults to 'm'. */
  readonly lengthUnit?: string;
  /** Swatch colour (grey placeholder until the user picks one). */
  readonly color?: string;
  /** When true, shows a red outline (invalid or duplicate value after commit). */
  readonly hasError?: boolean;
  /** Red inline error copy shown beneath the value field when `hasError` is true. */
  readonly errorMessage?: string;
  /** When true, paints the swatch focus ring (e.g. colour picker open). */
  readonly swatchSelected?: boolean;
  /** Anchor for positioning a colour-picker popover beside the swatch. */
  swatchContainerRef?(el: HTMLDivElement): void;
  onValueInput(value: string): void;
  onSubmit(value: string): void;
  onValueBlur?(value: string): void;
  onSwatchClick?(): void;
}

// Neutral level-2 surface colour used while no stop colour has been chosen yet.
export const PLACEHOLDER_SWATCH_COLOR = '#e9eaf0';

/**
 * Presentational input for adding a new colour stop: a swatch, a value field
 * (with unit suffix) and helper text. This is a dumb component — all state and
 * behaviour are driven by props; it holds no store or add logic of its own.
 */
export const NewColorStopInput: Component<NewColorStopInputProps> = (props) => {
  function handleKeyDown(e: KeyboardEvent): void {
    if (blockPaletteStopExponentKey(e)) return;
    if (e.key !== 'Enter') return;
    e.preventDefault();
    props.onSubmit(props.value);
  }

  const inputErrorClass = (): string | undefined =>
    props.hasError ? NEW_STOP_INPUT_ERROR_CLASS : undefined;

  return (
    <div
      class="flex flex-col gap-1.5 pb-4 border-b border-gray-0"
      data-testid="new-color-stop-input"
    >
      <span class="text-sm text-gray-6">
        {t('measurements.volumetric.gradient.newStopHelp')}
      </span>
      <div class="flex items-center gap-1.5">
        <div
          class="relative shrink-0"
          ref={(el) => props.swatchContainerRef?.(el)}
        >
          <ColorSwatch
            color={props.color ?? PLACEHOLDER_SWATCH_COLOR}
            testId="new-color-stop-swatch"
            selected={props.swatchSelected === true}
            onClick={
              props.onSwatchClick ? () => props.onSwatchClick?.() : undefined
            }
          />
        </div>
        <modus-wc-number-input
          class={`${NEW_STOP_NUMBER_INPUT_CLASS} ${PALETTE_STOP_NUMBER_INPUT_CLASS}`}
          attr:custom-class={inputErrorClass()}
          bordered={true}
          label=""
          size="sm"
          // @ts-expect-error -- HTML step="any" allows arbitrary decimals; Modus types step as number only
          step="any"
          value={props.value}
          placeholder={t('measurements.volumetric.gradient.newStopPlaceholder')}
          attr:aria-invalid={props.hasError ? 'true' : undefined}
          attr:aria-describedby={
            props.errorMessage ? 'new-color-stop-value-error' : undefined
          }
          attr:data-testid="new-color-stop-value"
          attr:data-has-error={props.hasError ? 'true' : undefined}
          on:inputChange={(event) =>
            props.onValueInput(readModusInputChangeValue(event))
          }
          on:inputBlur={(event) =>
            props.onValueBlur?.(readModusInputChangeValue(event))
          }
          onKeyDown={handleKeyDown}
        />
        <span class="text-sm text-gray-6 shrink-0">
          {props.lengthUnit ?? 'm'}
        </span>
      </div>
      <Show when={props.errorMessage}>
        <modus-wc-input-feedback
          id="new-color-stop-value-error"
          level="error"
          attr:message={props.errorMessage ?? ''}
          data-testid="new-color-stop-value-error"
        />
      </Show>
    </div>
  );
};
